import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BlueprintService } from './blueprint.service';
import { TemplateEditorService } from './template-editor.service';
import { cloneDeep } from 'lodash';

const CUSTOMIZATION = 'customization';

@Injectable({
  providedIn: 'root',
})
export class AttributeDataService {
  static readonly DEFAULT_CANVAS_WIDTH = 1920;
  static readonly DEFAULT_CANVAS_HEIGHT = 1080;

  constructor(
    private blueprintService: BlueprintService,
    private templateEditorService: TemplateEditorService,
  ) {}

  getBlueprintData(componentId, attributeKey?) {
    return this.blueprintService.getBlueprintData(componentId, attributeKey);
  }

  getAttributeData(componentId, attributeKey?) {
    if (!componentId) {
      return null;
    }

    var component = this._componentFor(componentId, false);

    if (component.element && component.element.attributes) {
      return attributeKey
        ? component.element.attributes[attributeKey]
        : component.element.attributes;
    } else {
      // if the attributeKey is not provided, it returns the full component structure
      return attributeKey ? component[attributeKey] : component;
    }
  }

  getAvailableAttributeData(componentId, attributeName) {
    var result = this.getAttributeData(componentId, attributeName);

    if (result === undefined) {
      result = this.getBlueprintData(componentId, attributeName);
    }

    return result;
  }

  setAttributeData(componentId, attributeKey, value) {
    if (!componentId) {
      return null;
    }

    var component = this._componentFor(componentId, true);

    if (component.element) {
      if (!component.element.attributes) {
        component.element.attributes = {};
      }

      component.element.attributes[attributeKey] = value;
    } else {
      component[attributeKey] = value;
    }
  }

  setMultipleAttributeData(
    componentId: string,
    attributes: { [attributeKey: string]: any },
  ) {
    if (!componentId) {
      return null;
    }

    const component = this._componentFor(componentId, true);

    if (component.element) {
      if (!component.element.attributes) {
        component.element.attributes = {};
      }
      Object.keys(attributes).forEach((attributeKey) => {
        component.element.attributes[attributeKey] = attributes[attributeKey];
      });
    } else {
      Object.keys(attributes).forEach((attributeKey) => {
        component[attributeKey] = attributes[attributeKey];
      });
    }
  }

  getAttributeDataGlobal(attributeKey) {
    return this.templateEditorService.presentation.templateAttributeData[
      attributeKey
    ];
  }

  setAttributeDataGlobal(attributeKey, value) {
    this.templateEditorService.presentation.templateAttributeData[
      attributeKey
    ] = value;
  }

  // Mirrors PresentationPreviewComponent's _getPresetDimensions/
  // _getTemplateWidth/_getTemplateHeight: templateAttributeData.width/height
  // is the blueprint's original design size and is only ever overwritten
  // with the live orientation preset when orientationPreset is 'custom' —
  // for 'portrait'/'landscape' it stays stale, so callers that need the
  // canvas's actual current size must go through the preset first.
  getCanvasDimensions(): { width: number; height: number } {
    const attributeData = this.templateEditorService.presentation
      ?.templateAttributeData as { orientationPreset?: string } | undefined;
    const orientationPreset = attributeData?.orientationPreset;

    if (orientationPreset === 'portrait' || orientationPreset === 'custom') {
      const width = Number(attributeData?.['widthPreset']);
      const height = Number(attributeData?.['heightPreset']);

      if (width && height) {
        return { width, height };
      }
    }

    const blueprintWidth = parseInt(
      this.blueprintService.blueprintData?.width,
      10,
    );
    const blueprintHeight = parseInt(
      this.blueprintService.blueprintData?.height,
      10,
    );

    return {
      width: blueprintWidth || AttributeDataService.DEFAULT_CANVAS_WIDTH,
      height: blueprintHeight || AttributeDataService.DEFAULT_CANVAS_HEIGHT,
    };
  }

  getCustomization(componentId, attributeKey) {
    const customization =
      this.getAttributeData(componentId, CUSTOMIZATION) || {};
    return customization[attributeKey];
  }

  setCustomization(componentId, attributeKey, value) {
    const customization =
      this.getAttributeData(componentId, CUSTOMIZATION) || {};
    customization[attributeKey] = value;
    this.setAttributeData(componentId, CUSTOMIZATION, customization);
  }

  getUserComponents() {
    var attributeData =
      this.templateEditorService.presentation?.templateAttributeData;
    var userComponents = [];

    if (attributeData?.components && attributeData.components.length) {
      userComponents = attributeData.components.filter(
        (comp) => comp.userComponent,
      );
    }

    return userComponents;
  }

  moveComponentsIntoView(viewportWidth: number, viewportHeight: number): void {
    const userComponents = this.getUserComponents();

    userComponents.forEach((component) => {
      const customization = component.customization;
      if (!customization) {
        return;
      }

      const hasPosition = customization.left && customization.top;

      if (!hasPosition) {
        return;
      }

      const left = parseFloat(customization.left) || 0;
      const top = parseFloat(customization.top) || 0;
      const width = parseFloat(customization.width) || 0;
      const height = parseFloat(customization.height) || 0;

      const adjusted = this._adjustPositionIntoView(
        left,
        top,
        width,
        height,
        viewportWidth,
        viewportHeight,
      );

      if (adjusted.left === left && adjusted.top === top) {
        return;
      }

      this.setAttributeData(component.id, CUSTOMIZATION, {
        ...customization,
        left: `${adjusted.left}px`,
        top: `${adjusted.top}px`,
      });
    });
  }

  private _adjustPositionIntoView(
    left: number,
    top: number,
    width: number,
    height: number,
    viewportWidth: number,
    viewportHeight: number,
  ): { left: number; top: number } {
    let newLeft = left;
    let newTop = top;

    if (newLeft + width > viewportWidth) {
      newLeft = viewportWidth - width;
    }

    if (newTop + height > viewportHeight) {
      newTop = viewportHeight - height;
    }

    return {
      left: Math.max(0, newLeft),
      top: Math.max(0, newTop),
    };
  }

  addUserComponent(addedComponent) {
    if (!addedComponent.id || !addedComponent.userComponent) {
      return null;
    }

    var attributeData =
      this.templateEditorService.presentation.templateAttributeData;
    var component;

    if (attributeData.components) {
      component = _.find(attributeData.components, {
        id: addedComponent.id,
      });

      // check the component doesn't already exist
      if (!component) {
        // ensure component is added to beginning/top of list
        attributeData.components.unshift(addedComponent);
      }
    } else {
      attributeData.components = [addedComponent];
    }
  }

  removeUserComponent(id) {
    if (!id) {
      return null;
    }

    var attributeData =
      this.templateEditorService.presentation.templateAttributeData;

    if (attributeData.components) {
      attributeData.components = attributeData.components.filter(
        (comp) => comp.id !== id,
      );
    }
  }

  reorderUserComponents(ordered) {
    if (!ordered || !Array.isArray(ordered)) {
      return null;
    }

    const userComponents = this.getUserComponents();
    const reordered = cloneDeep(userComponents);
    const attributeData =
      this.templateEditorService.presentation.templateAttributeData;

    // clear all user components currently saved
    userComponents.forEach((userComponent) =>
      this.removeUserComponent(userComponent.id),
    );

    reordered.sort((a, b) => {
      return ordered.indexOf(a.id) - ordered.indexOf(b.id);
    });

    // re-add all user components in new order
    reordered.forEach((component) => attributeData.components.push(component));
  }

  getComponent(componentId: string) {
    let component = this.blueprintService.componentFor(componentId);

    if (!component) {
      component = this._componentFor(componentId, null);
    }

    return component;
  }

  // updateAttributeData: do not update the object on getAttributeData
  // or it will unnecessarily trigger hasUnsavedChanges = true
  _componentFor(componentId, updateAttributeData) {
    var attributeData =
      this.templateEditorService.presentation.templateAttributeData;
    var component;

    if (componentId.indexOf(' ') !== -1) {
      var tokens = componentId.split(' ');
      var playlistId = tokens[0];
      var playlist = this._componentFor(playlistId, updateAttributeData);

      if (playlist.items) {
        return playlist.items[tokens[1]];
      }
    } else if (attributeData.components) {
      component = _.find(attributeData.components, {
        id: componentId,
      });
    } else if (updateAttributeData) {
      attributeData.components = [];
    }

    if (!component) {
      component = {
        id: componentId,
      };

      var blueprintComponent = this.blueprintService.componentFor(componentId);

      // Retrieve playlist items from the blueprint
      if (blueprintComponent && blueprintComponent.type === 'rise-playlist') {
        component.items = this.getBlueprintData(componentId, 'items');
      }

      if (updateAttributeData) {
        attributeData.components.push(component);
      }
    }

    return component;
  }

  getComponentIds(filter?): string[] {
    if (!this.blueprintService.blueprintData) {
      return [];
    }
    const components = this.blueprintService.blueprintData.components;

    const filteredComponents = _.filter(components, filter);

    return _.map(filteredComponents, (component: any) => {
      return component.id;
    });
  }
}
